import { Spin } from "antd";
import type { LineConfig } from "./LineChartCard";
import styles from "./SectorHorizontalBarCard.module.css";

export interface SectorHorizontalBarCardProps {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  series: LineConfig[];
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
}

interface SectorRow {
  key: string;
  label: string;
  color: string;
  values: number[];
  firstValue: number;
  lastValue: number;
  averageValue: number;
  delta: number;
}

const getNumericValue = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value: number) => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1).replace(/\.0$/, "");
};

const formatDelta = (value: number) => {
  const sign = value > 0 ? "+" : "";
  const formattedValue = Math.abs(value) < 0.05
    ? "0"
    : value.toFixed(1).replace(/\.0$/, "");

  return `${sign}${formattedValue}`;
};

const getSparklineY = (
  value: number,
  min: number,
  range: number,
  height: number,
  verticalPadding: number
) => {
  const ratio = range === 0 ? 0.5 : (value - min) / range;
  return height - verticalPadding - ratio * (height - verticalPadding * 2);
};

const buildSparklineGeometry = (values: number[], width: number, height: number) => {
  if (values.length === 0) return { points: "", averageY: height / 2 };
  if (values.length === 1) return { points: `${width / 2},${height / 2}`, averageY: height / 2 };

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const horizontalStep = width / (values.length - 1);
  const verticalPadding = 5;
  const averageValue = values.reduce((total, value) => total + value, 0) / values.length;

  const points = values
    .map((value, index) => {
      const x = index * horizontalStep;
      const y = getSparklineY(value, min, range, height, verticalPadding);

      return `${x},${y}`;
    })
    .join(" ");

  return {
    points,
    averageY: getSparklineY(averageValue, min, range, height, verticalPadding),
  };
};

const buildRows = (
  data: Record<string, string | number>[],
  series: LineConfig[]
): SectorRow[] => {
  return series
    .map(({ key, label, color }) => {
      const values = data
        .map((point) => getNumericValue(point[key]))
        .filter((value): value is number => value !== null);

      if (values.length === 0) return null;

      const firstValue = values[0];
      const lastValue = values[values.length - 1];
      const averageValue = values.reduce((total, value) => total + value, 0) / values.length;

      return {
        key,
        label,
        color,
        values,
        firstValue,
        lastValue,
        averageValue,
        delta: lastValue - firstValue,
      };
    })
    .filter((row): row is SectorRow => row !== null)
    .sort((a, b) => b.lastValue - a.lastValue);
};

const getBadgeClass = (value: number) => {
  if (value > 0.05) return styles.badgeUp;
  if (value < -0.05) return styles.badgeDown;
  return styles.badgeFlat;
};

const SectorHorizontalBarCard = ({
  title,
  data,
  xKey,
  series,
  loading = false,
  error = null,
  headerExtra,
}: SectorHorizontalBarCardProps) => {
  const rows = buildRows(data, series);
  const firstPeriod = data[0]?.[xKey];
  const lastPeriod = data[data.length - 1]?.[xKey];
  const maxLastValue = rows.length > 0 ? Math.max(...rows.map((row) => row.lastValue)) : 0;

  return (
    <div className={styles.card} data-chart-card>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        {headerExtra}
      </div>

      {loading && (
        <div className={styles.centered}>
          <Spin size="small" /> Carregant dades…
        </div>
      )}
      {!loading && error && (
        <div className={styles.centered}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className={styles.centered}>
          <p className={styles.emptyMessage}>Sense dades disponibles</p>
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Sector</th>
                <th>{firstPeriod ?? "Inicial"}</th>
                <th>{lastPeriod ?? "Final"}</th>
                <th>Canvi</th>
                <th>Mitjana</th>
                <th>Tendència</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const barWidth = maxLastValue > 0
                  ? Math.max(3, (row.lastValue / maxLastValue) * 100)
                  : 0;
                const sparklineGeometry = buildSparklineGeometry(row.values, 120, 34);
                const sparklinePoints = sparklineGeometry.points;
                const firstPoint = sparklinePoints.split(" ")[0];
                const lastPoint = sparklinePoints.split(" ").slice(-1)[0];

                return (
                  <tr key={row.key}>
                    <td className={styles.sectorCell}>
                      <div className={styles.sectorContent}>
                        <div className={styles.sectorHeader}>
                          <span className={styles.sectorName}>
                            <span className={styles.dot} style={{ backgroundColor: row.color }} />
                            {row.label}
                          </span>
                          <span className={styles.sectorValue}>{formatValue(row.lastValue)}</span>
                        </div>
                        <span className={styles.barTrack} aria-hidden="true">
                          <span
                            className={styles.bar}
                            style={{ width: `${barWidth}%`, backgroundColor: row.color }}
                          />
                        </span>
                      </div>
                    </td>
                    <td className={styles.valueCell}>{formatValue(row.firstValue)}</td>
                    <td className={styles.valueCell}>{formatValue(row.lastValue)}</td>
                    <td>
                      <span className={`${styles.badge} ${getBadgeClass(row.delta)}`}>
                        {formatDelta(row.delta)}
                      </span>
                    </td>
                    <td className={styles.valueCell}>{formatValue(row.averageValue)}</td>
                    <td>
                      <svg
                        className={styles.sparkline}
                        viewBox="0 0 120 34"
                        role="img"
                        aria-label={`${row.label}: ${formatValue(row.firstValue)} a ${formatValue(row.lastValue)}`}
                      >
                        <line
                          className={styles.sparklineGuide}
                          x1="0"
                          y1={sparklineGeometry.averageY}
                          x2="120"
                          y2={sparklineGeometry.averageY}
                        />
                        <polyline
                          className={styles.sparklineLine}
                          points={sparklinePoints}
                          stroke={row.color}
                        />
                        {firstPoint && (
                          <circle
                            className={styles.sparklineDot}
                            cx={firstPoint.split(",")[0]}
                            cy={firstPoint.split(",")[1]}
                            r="3"
                            fill={row.color}
                          />
                        )}
                        {lastPoint && (
                          <circle
                            className={styles.sparklineDot}
                            cx={lastPoint.split(",")[0]}
                            cy={lastPoint.split(",")[1]}
                            r="3"
                            fill={row.color}
                          />
                        )}
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SectorHorizontalBarCard;
